import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { updateAccountStripeBillingInformation } from 'api/account';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Account } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface StripeBillingSettingsFormValues {
    billing_email: string;
}

const StripeBillingSettings = () => {
    const toast = useToast();
    const { accountId, billingEmail } = useCurrentAccount();

    const defaultValues: StripeBillingSettingsFormValues = {
        billing_email: billingEmail || '',
    };

    const methods = Form.useGivebutterForm<StripeBillingSettingsFormValues>({ defaultValues });
    const { control, handleSubmit, setError, resetFormButton, path, watch } = methods;
    const stripeBillingEmail = watch('billing_email');

    const { mutate: submit, isPending: isLoading } = useMutation<
        Account,
        GivebutterAxiosError,
        StripeBillingSettingsFormValues
    >({
        mutationFn: values =>
            updateAccountStripeBillingInformation(accountId, {
                billing_email: values.billing_email,
            }),
        onSuccess: () => {
            toast.success('Stripe billing information updated.');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            const errors = data?.errors;

            toast.error(data?.message ?? 'Failed to update Stripe billing information.');

            if (typeof errors === 'object') {
                for (let field in errors) {
                    setError(field as keyof StripeBillingSettingsFormValues, {
                        message: errors[field][0],
                    });
                }
            }
            resetFormButton();
        },
    });

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>Billing email</SettingsCard.Heading>
                <p className="text-sm text-gray-700">
                    Update the email address used for invoices related to Givebutter Plus and
                    Mailings.
                </p>
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide>
                <Form.Provider {...methods}>
                    <form
                        onSubmit={handleSubmit(values => submit(values))}
                        className="flex flex-col gap-4"
                    >
                        <Form.Container>
                            <Form.Label className="text-base" name={path('billing_email')}>
                                Email
                            </Form.Label>
                            <Form.TextInputRoot control={control} name="billing_email">
                                <Form.TextInputForm placeholder="Enter Stripe billing email" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('billing_email')} />
                        </Form.Container>

                        <Form.Container className="flex-row">
                            <Form.SubmitButton
                                disabled={
                                    !(stripeBillingEmail && stripeBillingEmail?.trim().length > 0)
                                }
                                isSubmitting={isLoading}
                            >
                                Save
                            </Form.SubmitButton>
                        </Form.Container>
                    </form>
                </Form.Provider>
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

export { StripeBillingSettings };
